/**
 * Social Proof Widget for Shopify – Ecomus Theme
 * ================================================
 * Drop this file into: Assets/social-proof-widget.js
 * Then add {% render 'social-proof-widget' %} in your product snippet
 * OR include via theme.liquid just before </body>
 *
 * Usage:
 *   <script src="{{ 'social-proof-widget.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  /* ─── CONFIG ─────────────────────────────────────────── */
  const CONFIG = {
    // Range for "currently viewing" count
    viewersMin: 8,
    viewersMax: 34,

    // Range for "added to cart" count
    cartMin: 3,
    cartMax: 17,

    // How often the numbers change (ms)
    updateInterval: 5000,

    // Small random drift per tick (±N)
    viewersDrift: 3,
    cartDrift: 2,

    // Pulse animation duration (ms)
    pulseDuration: 600,

    // Widget position on page – CSS selector of element to insert AFTER
    // Targets Ecomus product form area; adjust if your theme differs
    insertAfterSelector: '.tf-product-info-price, .product__price, .price-section, .product-form__buttons',

    // Fallback: insert after product title
    fallbackSelector: '.product__title, .product-single__title, h1.title',

    // Only show on product pages
    onlyOnProductPages: true,
  };
  /* ─────────────────────────────────────────────────────── */

  /* Check if we're on a product page */
  function isProductPage() {
    return (
      document.body.classList.contains('template-product') ||
      window.location.pathname.includes('/products/')
    );
  }

  if (CONFIG.onlyOnProductPages && !isProductPage()) return;

  /* ─── STATE ───────────────────────────────────────────── */
  let viewers = rand(CONFIG.viewersMin, CONFIG.viewersMax);
  let cartCount = rand(CONFIG.cartMin, CONFIG.cartMax);

  function rand(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  function clamp(val, min, max) {
    return Math.min(Math.max(val, min), max);
  }

  function drift(current, delta, min, max) {
    const change = Math.floor(Math.random() * (delta * 2 + 1)) - delta;
    return clamp(current + change, min, max);
  }

  /* ─── BUILD WIDGET HTML ───────────────────────────────── */
  function buildWidget() {
    const wrapper = document.createElement('div');
    wrapper.id = 'spw-root';
    wrapper.setAttribute('aria-live', 'polite');
    wrapper.setAttribute('aria-label', 'Social proof information');

    wrapper.innerHTML = `
      <style>
        #spw-root {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin: 14px 0 18px;
          font-family: inherit;
        }
        .spw-row {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          background: #f7f7f5;
          border-radius: 8px;
          border: 1px solid #e8e6e1;
          font-size: 13.5px;
          color: #2c2c2a;
          line-height: 1.4;
          transition: background 0.3s;
        }
        @media (prefers-color-scheme: dark) {
          .spw-row {
            background: #1e1e1c;
            border-color: #2e2e2c;
            color: #e8e6e1;
          }
        }
        .spw-icon {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .spw-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #2ecc71;
          box-shadow: 0 0 0 0 rgba(46,204,113,0.4);
          animation: spw-pulse-dot 2s infinite;
          flex-shrink: 0;
        }
        .spw-dot-cart {
          background: #e67e22;
          box-shadow: 0 0 0 0 rgba(230,126,34,0.4);
          animation: spw-pulse-dot-cart 2s infinite;
        }
        @keyframes spw-pulse-dot {
          0%   { box-shadow: 0 0 0 0 rgba(46,204,113,0.45); }
          70%  { box-shadow: 0 0 0 6px rgba(46,204,113,0); }
          100% { box-shadow: 0 0 0 0 rgba(46,204,113,0); }
        }
        @keyframes spw-pulse-dot-cart {
          0%   { box-shadow: 0 0 0 0 rgba(230,126,34,0.45); }
          70%  { box-shadow: 0 0 0 6px rgba(230,126,34,0); }
          100% { box-shadow: 0 0 0 0 rgba(230,126,34,0); }
        }
        .spw-number {
          font-weight: 700;
          font-size: 15px;
          min-width: 22px;
          display: inline-block;
          transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1), opacity 0.2s;
        }
        .spw-number.spw-bump {
          transform: scale(1.35);
          opacity: 0.6;
        }
        .spw-label {
          flex: 1;
          color: inherit;
          opacity: 0.85;
        }
        .spw-label strong {
          color: inherit;
          opacity: 1;
          font-weight: 600;
        }
        .spw-badge {
          font-size: 11px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 20px;
          letter-spacing: 0.02em;
        }
        .spw-badge-green {
          background: #d5f5e3;
          color: #1e7a45;
        }
        .spw-badge-orange {
          background: #fde8cc;
          color: #8a4a08;
        }
        @media (prefers-color-scheme: dark) {
          .spw-badge-green  { background: #0e3d22; color: #74dfa0; }
          .spw-badge-orange { background: #3d2208; color: #f0a95c; }
        }
      </style>

      <!-- Viewers row -->
      <div class="spw-row" id="spw-viewers-row">
        <span class="spw-dot" aria-hidden="true"></span>
        <svg class="spw-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
        <span class="spw-label">
          <strong><span id="spw-viewers" class="spw-number">${viewers}</span> شخص</strong>
          يشاهد هذا المنتج الآن
        </span>
        <span class="spw-badge spw-badge-green">مباشر</span>
      </div>

      <!-- Cart row -->
      <div class="spw-row" id="spw-cart-row">
        <span class="spw-dot spw-dot-cart" aria-hidden="true"></span>
        <svg class="spw-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
        </svg>
        <span class="spw-label">
          <strong><span id="spw-cart" class="spw-number">${cartCount}</span> شخص</strong>
          أضاف المنتج للسلة
        </span>
        <span class="spw-badge spw-badge-orange">مرتفع</span>
      </div>
    `;

    return wrapper;
  }

  /* ─── ANIMATE NUMBER CHANGE ──────────────────────────── */
  function animateNumber(el, newVal) {
    el.classList.add('spw-bump');
    setTimeout(() => {
      el.textContent = newVal;
      el.classList.remove('spw-bump');
    }, 150);
  }

  /* ─── UPDATE TICK ─────────────────────────────────────── */
  function tick() {
    const viewersEl = document.getElementById('spw-viewers');
    const cartEl    = document.getElementById('spw-cart');
    if (!viewersEl || !cartEl) return;

    const newViewers = drift(viewers,   CONFIG.viewersDrift, CONFIG.viewersMin, CONFIG.viewersMax);
    const newCart    = drift(cartCount, CONFIG.cartDrift,    CONFIG.cartMin,    CONFIG.cartMax);

    if (newViewers !== viewers) {
      viewers = newViewers;
      animateNumber(viewersEl, viewers);
    }
    if (newCart !== cartCount) {
      cartCount = newCart;
      animateNumber(cartEl, cartCount);
    }
  }

  /* ─── INSERT INTO PAGE ────────────────────────────────── */
  function insert() {
    // Already injected?
    if (document.getElementById('spw-root')) return;

    const selectors = [
      CONFIG.insertAfterSelector,
      CONFIG.fallbackSelector,
    ].join(', ');

    const target = document.querySelector(selectors);

    if (target) {
      target.insertAdjacentElement('afterend', buildWidget());
    } else {
      // Last resort: prepend to product description
      const desc = document.querySelector('.product__description, .product-single__description, .rte');
      if (desc) desc.prepend(buildWidget());
    }

    setInterval(tick, CONFIG.updateInterval);
  }

  /* ─── INIT ────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', insert);
  } else {
    insert();
  }

  // Support Shopify's section rendering (theme editor live preview)
  document.addEventListener('shopify:section:load', insert);
})();